import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail, ArrowLeft, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { OTPVerification } from "./OTPVerification";

interface EmailSignUpProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const EmailSignUp = ({ onBack, onSuccess }: EmailSignUpProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 6) return "Password must be at least 6 characters";
    return null;
  };

  const handleSignUp = async () => {
    if (!email.trim()) {
      setError(t("entry.pleaseEnterEmail"));
      return;
    }
    if (!validateEmail(email)) {
      setError(t("entry.pleaseEnterValidEmail"));
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if email already exists in profiles
      const { data: existingProfile } = await (supabase
        .from("profiles") as any)
        .select("id")
        .eq("email", email.toLowerCase())
        .maybeSingle();

      if (existingProfile) {
        setError(t("entry.alreadyRegistered") || "This email is already registered. Please sign in instead.");
        setIsLoading(false);
        return;
      }

      // Create user with email + password (Supabase sends confirmation OTP)
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.toLowerCase(),
        password,
        // Do NOT set emailRedirectTo — forces Supabase to send OTP code instead of magic link
      });

      if (signUpError) {
        if (
          signUpError.message.includes("already registered") ||
          signUpError.message.includes("already exists") ||
          signUpError.message.includes("User already registered")
        ) {
          setError(t("entry.alreadyRegistered") || "This email is already registered. Please sign in instead.");
        } else {
          setError(signUpError.message);
        }
        setIsLoading(false);
        return;
      }

      // Show OTP verification screen
      setShowOTP(true);
      toast.success((t("otp.codeSentToIdentifier") || "Verification code sent to") + " " + email);
    } catch (err: any) {
      console.error("Error during sign up:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async (otp: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.toLowerCase(),
        token: otp,
        type: "signup",
      });

      if (error) {
        return { success: false, error: error.message || "Invalid verification code" };
      }

      if (data.user) {
        // Update profile with provider and email_verified
        await (supabase.from("profiles") as any)
          .update({
            email: email.toLowerCase(),
            provider: "email",
            email_verified: true,
            last_login: new Date().toISOString(),
          })
          .eq("id", data.user.id);

        return { success: true };
      }

      return { success: false, error: t("entry.verificationFailed") || "Verification failed" };
    } catch (err: any) {
      return { success: false, error: err.message || "Verification failed" };
    }
  };

  const resendOTP = async (): Promise<void> => {
    // Resend by calling signUp again (Supabase handles resend)
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email.toLowerCase(),
    });
    if (error) {
      toast.error(error.message || "Failed to resend code");
    }
  };

  if (showOTP) {
    return (
      <OTPVerification
        type="email"
        identifier={email}
        onVerify={verifyOTP}
        onResend={resendOTP}
        onBack={() => setShowOTP(false)}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t("entry.back")}</span>
      </button>

      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("entry.signUpWithEmailTitle") || "Create your account"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("entry.enterEmailAndPassword") || "Enter your email and create a password"}
        </p>
      </div>

      {/* Email Input */}
      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          placeholder={t("entry.emailAddress") || "Email address"}
          disabled={isLoading}
          className={cn(
            "w-full pl-12 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
            error && "ring-2 ring-destructive",
            isLoading && "opacity-50 cursor-not-allowed"
          )}
        />
      </div>

      {/* Password Input */}
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
          }}
          placeholder={t("entry.password") || "Password (min 6 characters)"}
          disabled={isLoading}
          className={cn(
            "w-full pl-12 pr-12 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
            isLoading && "opacity-50 cursor-not-allowed"
          )}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {error && (
        <p className="text-xs text-destructive ml-1" role="alert">
          {error}
        </p>
      )}

      {/* Create Account Button */}
      <button
        onClick={handleSignUp}
        disabled={isLoading || !email.trim() || !password}
        className={cn(
          "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
          "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
          "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
          "hover:scale-[1.02] active:scale-[0.98] transition-all",
          "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        )}
      >
        {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
        {isLoading ? (t("entry.creating") || "Creating...") : (t("entry.createAccount") || "Create Account")}
      </button>
    </div>
  );
};
